import { type FC, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Building2Icon, CheckIcon, FolderUpIcon, House, SearchIcon } from 'lucide-react';

import { useEstateOptions } from '@/api/estate-options';
import { Input } from '@/components/ui/input';
import Spinner from '@/components/ui/spinner';
import { type SearchAllResult, useSearchAllInfinite } from '@/features/search/api/search';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';
import { cn } from '@/lib/utils';
import type { EstateProjectSearchItem } from './upload-floor-plan/types';

const PAGE_SIZE = 20;

type EstateProjectSearchProps = {
  selectedItem?: EstateProjectSearchItem;
  onSelect: (item: EstateProjectSearchItem) => void;
};

const toSearchItems = (
  data: SearchAllResult | undefined,
  fallbackName: string
): EstateProjectSearchItem[] => {
  return [
    ...(data?.estates ?? []).map((estate) => ({
      id: estate.id,
      name: estate.name ?? fallbackName,
      type: 'estate' as const,
      propertyType: estate.estateDetails?.propertyType ?? undefined,
    })),
    ...(data?.projects ?? []).map((project) => ({
      id: project.id,
      name: project.name ?? fallbackName,
      type: 'project' as const,
      propertyType: project.details?.propertyType ?? undefined,
    })),
  ];
};

const deduplicateSearchItems = (items: EstateProjectSearchItem[]) => {
  const itemsMap = new Map<string, EstateProjectSearchItem>();

  for (const item of items) {
    itemsMap.set(`${item.type}-${item.id}`, item);
  }

  return Array.from(itemsMap.values());
};

export const EstateProjectSearch: FC<EstateProjectSearchProps> = ({ selectedItem, onSelect }) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [searchString, setSearchString] = useState('');
  const { data: estateOptions } = useEstateOptions();

  const {
    data: results,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useSearchAllInfinite({
    query: {
      searchType: 'all',
      page: 1,
      pageSize: PAGE_SIZE,
      filters: { searchString },
    },
  });

  const { lastElementRef, pageNum, resetPageNum } = useInfiniteScroll<HTMLDivElement>({
    loading: isLoading || isFetchingNextPage,
    stopLoad: !hasNextPage,
  });

  const debouncedSearch = useDebouncedCallback((value: string) => {
    resetPageNum();
    setSearchString(value);
  });

  const fallbackName = t('noName');

  const items = useMemo(
    () =>
      deduplicateSearchItems(
        (results?.pages ?? []).flatMap((page) => toSearchItems(page, fallbackName))
      ),
    [fallbackName, results?.pages]
  );

  useEffect(() => {
    if (pageNum <= 1 || !hasNextPage || isFetchingNextPage) return;

    void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, pageNum]);

  const isSelected = (item: EstateProjectSearchItem) =>
    selectedItem?.id === item.id && selectedItem?.type === item.type;

  const propertyTypeMap = useMemo(
    () =>
      estateOptions?.propertyType.reduce<Record<string, string>>((acc, propertyType) => {
        acc[propertyType.id] = propertyType.title;
        return acc;
      }, {}) ?? {},
    [estateOptions?.propertyType]
  );

  const showEmptyState = !isLoading && !isFetchingNextPage && !hasNextPage && !items.length;

  return (
    <div className="flex min-h-0 flex-col gap-3">
      <p className="text-sm font-medium text-slate-500">{t('whichEstateIsTheFloorPlanFor')}</p>

      <Input
        size="sm"
        value={inputValue}
        placeholder={t('searchEstates')}
        startIcon={<SearchIcon className="size-4 text-slate-500" />}
        className="border-slate-200 text-sm text-slate-600 placeholder:text-slate-500 focus-visible:border-primary-light"
        onChange={(event) => {
          setInputValue(event.target.value);
          debouncedSearch(event.target.value);
        }}
      />

      <fieldset className="m-0 h-[16rem] min-h-0 min-w-0 space-y-2 overflow-y-auto border-0 p-0 pr-3">
        <legend className="sr-only">{t('whichEstateIsTheFloorPlanFor')}</legend>
        {isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Spinner className="size-10 [&_*]:stroke-primary-dark" />
          </div>
        ) : null}

        {showEmptyState ? (
          <Trans
            t={t}
            i18nKey="no-estates-found"
            components={{
              label: <span className="font-medium text-primary-light" />,
              wrapper: <div className="flex h-full flex-col-reverse items-center justify-center" />,
              icon: (
                <House
                  viewBox="-1 0 25 25"
                  width="38"
                  height="38"
                  className="[&_*]:stroke-primary-light"
                />
              ),
            }}
          />
        ) : null}

        {items.map((item) => (
          <button
            key={`${item.type}-${item.id}`}
            type="button"
            aria-pressed={isSelected(item)}
            className={cn(
              'flex min-h-14 w-full items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors',
              isSelected(item)
                ? 'border-primary-light bg-primary-light/5'
                : 'border-slate-200 hover:bg-slate-50'
            )}
            onClick={() => onSelect(item)}
          >
            <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-500">
              {item.type === 'estate' ? (
                <Building2Icon className="size-5" />
              ) : (
                <FolderUpIcon className="size-5" />
              )}
            </span>

            <span className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-semibold text-primary-dark">{item.name}</span>
              <span className="text-xs text-slate-500">
                {item.propertyType
                  ? (propertyTypeMap[item.propertyType] ?? t(item.propertyType))
                  : null}
              </span>
            </span>

            {isSelected(item) ? <CheckIcon className="ml-auto size-5 text-primary-light" /> : null}
          </button>
        ))}

        {hasNextPage ? <div ref={lastElementRef} className="h-1" /> : null}

        {isFetchingNextPage ? (
          <div className="flex h-12 items-center justify-center">
            <Spinner className="size-8 [&_*]:stroke-primary-dark" />
          </div>
        ) : null}
      </fieldset>
    </div>
  );
};
